/**
 * Spec 040: a staff member's feed, its archive and its live console.
 *
 * The feed: what the staff member did and what was done to it, wherever it runs, with who did it.
 * The archive: its files older than 7 days — read back for the screen, downloaded, deleted by a
 * person. The console: watched by confirming every 20 seconds, read with a long poll; only a
 * person types into it.
 */

import { api, fetchBlob } from "@/api/client";

export type FeedKind =
  | "turn"
  | "said"
  | "tool"
  | "result"
  | "tasker"
  | "helper"
  | "console"
  | "waits"
  | "answer"
  | "wrote"
  | "control"
  | "push";

export interface FeedEvent {
  id: number;
  at: string;
  kind: FeedKind;
  text: string;
  helper: string | null;
  run_id: string | null;
  session_id: string | null;
  /** «Костя», «Саня», «агент Кости», «агент Сани»; null — the staff member itself or the tasker. */
  by: string | null;
  has_detail: boolean;
  detail: string | null;
  detail_chars: number | null;
  payload: Record<string, unknown>;
}

export interface LimitWindow {
  used_percentage: number;
  resets_at: string | null;
  /** Reset since the snapshot: the share is old. */
  passed: boolean;
}

/** Spec 045: what the staff member does now and how much of the subscription is left. */
export interface StaffState {
  running: boolean;
  run_id: string;
  started_at: string;
  minutes: number;
  stopped_at: string | null;
  order: { id: string; number: number; title: string; state: string; since: string; minutes: number } | null;
  model: string | null;
  effort: string | null;
  five_hour: LimitWindow | null;
  seven_day: LimitWindow | null;
  limits_at: string | null;
  /** Spec 046: the PC of an open run in «Агенты», and whether it is still on the line. */
  pc_name?: string | null;
  pc_online?: boolean | null;
  pc_seen_at?: string | null;
  /** «Работает 34 мин · №12 «…» · opus · high · 5 ч: 23%, сброс 14:00 · …» */
  line: string;
}

export interface FeedPage {
  agent_id: string;
  events: FeedEvent[];
  last_id: number | null;
  more_before: boolean;
  state?: StaffState | null;
}

export interface FeedArchive {
  id: string;
  run_id: string | null;
  month: string;
  first_at: string;
  last_at: string;
  events: number;
  size: number;
}

export interface FeedArchiveRead {
  archive: FeedArchive;
  events: FeedEvent[];
  total: number;
  offset: number;
}

export interface ConsoleSize {
  cols: number;
  rows: number;
}

export interface ConsoleState {
  terminal_id: string;
  device_name: string;
  online: boolean;
  viewers: string[];
  start: number;
  end: number;
  size: ConsoleSize | null;
  alive: boolean;
}

export interface ConsoleChunk {
  terminal_id: string;
  begin: number;
  end: number;
  data: string;
  size: ConsoleSize | null;
  alive: boolean;
  exit_code: number | null;
  truncated: boolean;
}

export type ConsoleKey = "esc" | "up" | "down" | "left" | "right" | "enter" | "tab" | "ctrl_c";

export interface StaffMessageOut {
  sent: boolean;
  signature: string;
  terminal_id: string;
}

export interface FeedQuery {
  after?: number | null;
  before?: number | null;
  limit?: number;
  important?: boolean;
  withDetails?: boolean;
}

function feedPath(agentId: string, q: FeedQuery): string {
  const params = new URLSearchParams();
  if (q.after != null) params.set("after", String(q.after));
  if (q.before != null) params.set("before", String(q.before));
  if (q.limit != null) params.set("limit", String(q.limit));
  if (q.important) params.set("important", "true");
  if (q.withDetails) params.set("with_details", "true");
  const query = params.toString();
  return `/api/v1/agents/${agentId}/feed${query ? `?${query}` : ""}`;
}

export const Feed = {
  read: (agentId: string, q: FeedQuery = {}) => api<FeedPage>(feedPath(agentId, q)),
  /** One event with its details: the first of this staff member after the one before it. */
  detail: async (agentId: string, id: number): Promise<FeedEvent | null> => {
    const page = await api<FeedPage>(feedPath(agentId, { after: id - 1, limit: 1, withDetails: true }));
    return page.events.find((e) => e.id === id) ?? null;
  },
  /** «Написать сотруднику»: into its console signed «[Костя]: …», and into its feed. */
  write: (agentId: string, text: string) =>
    api<StaffMessageOut>(`/api/v1/agents/${agentId}/feed`, { method: "POST", body: { text } }),
  archives: (agentId: string) => api<FeedArchive[]>(`/api/v1/agents/${agentId}/feed/archive`),
  archive: (agentId: string, archiveId: string, offset = 0, limit = 200) =>
    api<FeedArchiveRead>(`/api/v1/agents/${agentId}/feed/archive/${archiveId}?offset=${offset}&limit=${limit}`),
  download: async (agentId: string, archive: FeedArchive): Promise<void> => {
    const blob = await fetchBlob(`/api/v1/agents/${agentId}/feed/archive/${archive.id}/file`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feed-${archive.month}-${archive.run_id ?? "no-run"}.jsonl.gz`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
  remove: (agentId: string, archiveId: string) =>
    api<void>(`/api/v1/agents/${agentId}/feed/archive/${archiveId}`, { method: "DELETE", body: { confirm: true } }),
};

export const Console = {
  watch: (agentId: string) =>
    api<ConsoleState>(`/api/v1/agents/${agentId}/console/watch`, { method: "POST", body: { run_id: null } }),
  read: (agentId: string, after: number, wait: number, signal?: AbortSignal) =>
    api<ConsoleChunk>(`/api/v1/agents/${agentId}/console?after=${after}&wait=${wait}`, { signal }),
  input: (agentId: string, data: string | null, keys: ConsoleKey[] = []) =>
    api<void>(`/api/v1/agents/${agentId}/console/input`, { method: "POST", body: { data, keys } }),
};
